const DARK_TEXT = '#1a1a1a';
const LIGHT_TEXT = '#ffffff';

const clampChannel = (value) => Math.min(255, Math.max(0, Math.round(value)));

const toHex = ({ r, g, b }) => (
  `#${[r, g, b]
    .map((channel) => clampChannel(channel).toString(16).padStart(2, '0'))
    .join('')}`
);

const parseColor = (value) => {
  const normalized = String(value || '').trim();
  const hexMatch = normalized.match(/^#([\da-f]{3}|[\da-f]{6}|[\da-f]{8})$/i);

  if (hexMatch) {
    let hex = hexMatch[1];
    if (hex.length === 3) {
      hex = hex.split('').map((character) => `${character}${character}`).join('');
    }
    if (hex.length === 8) hex = hex.slice(0, 6);

    return {
      r: Number.parseInt(hex.slice(0, 2), 16),
      g: Number.parseInt(hex.slice(2, 4), 16),
      b: Number.parseInt(hex.slice(4, 6), 16),
    };
  }

  const rgbMatch = normalized.match(
    /^rgba?\(\s*([\d.]+)\s*[, ]\s*([\d.]+)\s*[, ]\s*([\d.]+)/
  );
  if (!rgbMatch) return null;

  return {
    r: Number(rgbMatch[1]),
    g: Number(rgbMatch[2]),
    b: Number(rgbMatch[3]),
  };
};

const normalizeColor = (value, fallback) => {
  const parsed = parseColor(value) || parseColor(fallback);
  return parsed ? toHex(parsed) : fallback;
};

const relativeLuminance = (value) => {
  const color = parseColor(value);
  if (!color) return 0;

  const channels = [color.r, color.g, color.b].map((channel) => {
    const normalized = channel / 255;
    return normalized <= 0.04045
      ? normalized / 12.92
      : ((normalized + 0.055) / 1.055) ** 2.4;
  });

  return (0.2126 * channels[0]) + (0.7152 * channels[1]) + (0.0722 * channels[2]);
};

export const getPdfContrastRatio = (foreground, background) => {
  const foregroundLuminance = relativeLuminance(foreground);
  const backgroundLuminance = relativeLuminance(background);
  const lighter = Math.max(foregroundLuminance, backgroundLuminance);
  const darker = Math.min(foregroundLuminance, backgroundLuminance);

  return (lighter + 0.05) / (darker + 0.05);
};

const getContrastText = (background) => (
  getPdfContrastRatio(LIGHT_TEXT, background) >= getPdfContrastRatio(DARK_TEXT, background)
    ? LIGHT_TEXT
    : DARK_TEXT
);

const ensureReadableText = (foreground, background, fallback) => (
  getPdfContrastRatio(foreground, background) >= 4.5
    ? foreground
    : fallback
);

const mixColors = (first, second, secondWeight) => {
  const firstColor = parseColor(first);
  const secondColor = parseColor(second);
  if (!firstColor || !secondColor) return first;

  const weight = Math.min(1, Math.max(0, secondWeight));
  return toHex({
    r: (firstColor.r * (1 - weight)) + (secondColor.r * weight),
    g: (firstColor.g * (1 - weight)) + (secondColor.g * weight),
    b: (firstColor.b * (1 - weight)) + (secondColor.b * weight),
  });
};

const ensureSurfaceContrast = (background, textColor) => {
  let adjusted = background;
  const adjustmentTarget = textColor === LIGHT_TEXT ? '#000000' : LIGHT_TEXT;

  for (let step = 0; step < 20; step += 1) {
    if (getPdfContrastRatio(textColor, adjusted) >= 4.5) return adjusted;
    adjusted = mixColors(adjusted, adjustmentTarget, 0.02);
  }

  return adjusted;
};

export const createPdfPalette = (themeConfig) => {
  const apiColors = themeConfig?.colors || {};
  const page = LIGHT_TEXT;
  const ink = DARK_TEXT;
  const rawAccent = normalizeColor(apiColors.cta1, '#cd686a');
  const accent = ensureSurfaceContrast(rawAccent, getContrastText(rawAccent));
  const header = normalizeColor(apiColors.headerFooter, accent);
  const card = normalizeColor(apiColors.fondo, '#f6f7f9');
  const subtle = normalizeColor(apiColors.menuDesplegable, card);
  const muted = ensureReadableText(mixColors(ink, page, 0.32), page, ink);
  const cardText = getContrastText(card);
  const subtleText = getContrastText(subtle);

  return {
    page,
    ink,
    muted,
    header,
    headerText: getContrastText(header),
    accent,
    accentText: getContrastText(accent),
    accentOnPage: ensureReadableText(accent, page, ink),
    accentOnCard: ensureReadableText(accent, card, cardText),
    card,
    cardText,
    cardMuted: ensureReadableText(muted, card, cardText),
    subtle,
    subtleText,
    line: mixColors(subtle, subtleText, 0.14),
  };
};
